import { WindingDir } from "geom/core";
import { polyLine2WindingDir, polyLine2SetCCW, polyLine2Copy } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2AngleBetweenCW } from "geom/vec2";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_129: DocMigration = {
  maxSchemaVersion: "0.0.129",
  description: "Relative annotation positioning",
  apply(json: any) {
    for (const annotationKey in json.annotation) {
      const annotation = json.annotation[annotationKey];

      // replace original note annotation positioningMethod with textPositioningMethod
      if (annotation.positioningMethod) {
        annotation.textPositioningMethod = annotation.positioningMethod;
        delete annotation.positioningMethod;
      }

      //if add to front elevation, set to global
      if (annotation.addToFrontElevation === true) {
        annotation.positioningMethod = "GLOBAL";
      }

      //otherwise set to edge and update geometry
      else {
        annotation.positioningMethod = "EDGE";

        const roomId = annotation.parentId;
        const room = json.rooms[roomId];

        annotation.edgeId = room.edgeIds[0];

        //account for room outline winding dir
        const outline = room.outline;
        const outlineFinal =
          polyLine2WindingDir(outline) === WindingDir.CCW ? outline : polyLine2SetCCW(polyLine2Copy(outline));

        //get room x and y axis
        const xAxisStartX = outlineFinal[0];
        const xAxisStartY = outlineFinal[1];
        const xAxisEndX = outlineFinal[2];
        const xAxisEndY = outlineFinal[3];

        //angle between world x axis and room x axis
        const angle = vec2AngleBetweenCW(xAxisEndX - xAxisStartX, xAxisEndY - xAxisStartY, 1, 0);

        switch (annotation.type) {
          case "NOTE": {
            const { newX, newY } = transformPtFromGlobalToHost(
              xAxisStartX,
              xAxisStartY,
              annotation.x,
              annotation.y,
              angle,
            );
            annotation.x = newX;
            annotation.y = newY;

            // notes with leaders
            if (annotation.landingLineX !== undefined && annotation.landingLineY !== undefined) {
              const newLandingLinePts = transformPtFromGlobalToHost(
                xAxisStartX,
                xAxisStartY,
                annotation.landingLineX,
                annotation.landingLineY,
                angle,
              );
              annotation.landingLineX = newLandingLinePts.newX;
              annotation.landingLineY = newLandingLinePts.newY;
            }
            if (annotation.leaderLineX !== undefined && annotation.leaderLineY !== undefined) {
              const newLeaderLinePts = transformPtFromGlobalToHost(
                xAxisStartX,
                xAxisStartY,
                annotation.leaderLineX,
                annotation.leaderLineY,
                angle,
              );
              annotation.leaderLineX = newLeaderLinePts.newX;
              annotation.leaderLineY = newLeaderLinePts.newY;
            }

            const newRoatation = annotation.rotation - angle;
            annotation.rotation = newRoatation;
            break;
          }

          case "LINEWORK": {
            const shapes = annotation.shapes;
            for (const shapeId in shapes) {
              const shape = shapes[shapeId];
              const shapePts = shape.path;
              const newShapePts: number[] = [];
              for (let i = 0; i < shapePts.length; i += 2) {
                const { newX, newY } = transformPtFromGlobalToHost(
                  xAxisStartX,
                  xAxisStartY,
                  shapePts[i],
                  shapePts[i + 1],
                  angle,
                );
                newShapePts.push(newX);
                newShapePts.push(newY);
              }
              shape.path = polyLine2New(newShapePts);
            }
            break;
          }

          case "LINEAR_DIMENSION": {
            const { newX: newStartX, newY: newStartY } = transformPtFromGlobalToHost(
              xAxisStartX,
              xAxisStartY,
              annotation.startX,
              annotation.startY,
              angle,
            );
            const { newX: newEndX, newY: newEndY } = transformPtFromGlobalToHost(
              xAxisStartX,
              xAxisStartY,
              annotation.endX,
              annotation.endY,
              angle,
            );
            const { newX: newLineStartX, newY: newLineStartY } = transformPtFromGlobalToHost(
              xAxisStartX,
              xAxisStartY,
              annotation.lineStartX,
              annotation.lineStartY,
              angle,
            );
            const { newX: newLineEndX, newY: newLineEndY } = transformPtFromGlobalToHost(
              xAxisStartX,
              xAxisStartY,
              annotation.lineEndX,
              annotation.lineEndY,
              angle,
            );

            annotation.startX = newStartX;
            annotation.startY = newStartY;
            annotation.endX = newEndX;
            annotation.endY = newEndY;
            annotation.lineStartX = newLineStartX;
            annotation.lineStartY = newLineStartY;
            annotation.lineEndX = newLineEndX;
            annotation.lineEndY = newLineEndY;

            break;
          }

          case "DETAIL": {
            const { newX, newY } = transformPtFromGlobalToHost(
              xAxisStartX,
              xAxisStartY,
              annotation.x,
              annotation.y,
              angle,
            );

            annotation.x = newX;
            annotation.y = newY;

            break;
          }
        }
      }
    }

    return json;
  },
};

export default migration_0_0_129;

function transformPtFromGlobalToHost(
  hostOriginX: number,
  hostOriginY: number,
  ptX: number,
  ptY: number,
  angle: number,
) {
  const ptTranX = ptX - hostOriginX;
  const ptTranY = ptY - hostOriginY;

  let newX = ptTranX;
  let newY = ptTranY;

  if (angle !== 0) {
    angle = -angle;
    const cosTheta = Math.cos(angle);
    const sinTheta = Math.sin(angle);

    newX = ptTranX * cosTheta - ptTranY * sinTheta;
    newY = ptTranX * sinTheta + ptTranY * cosTheta;
  }
  return { newX, newY };
}
